/**
 * Gifts Page - NearU Gift Hub
 * ============================
 * Luxury gifting experience with simplified, elegant navigation.
 * Structure: Send a Gift Hero → Gift Experiences → NearU Card → Recent Gifts
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronRight, Lock, Apple, Gift, Sparkles, Clock,
  CreditCard, Snowflake, Eye, Crown, ArrowRight
} from 'lucide-react';
import NearULogo from '../components/NearULogo';
import NearUCard from '../components/NearUCard';
import IdentityVerificationModal from '../components/IdentityVerificationModal';
import GiftRevealModal from '../components/GiftRevealModal';
import MembershipBadge from '../components/MembershipBadge';
import { FEATURES } from '../config/featureFlags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GiftsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDark, setIsDark] = useState(true);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showGiftReveal, setShowGiftReveal] = useState(false);
  const [revealGift, setRevealGift] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Data state
  const [cardInfo, setCardInfo] = useState(null);
  const [balances, setBalances] = useState({ gifts: [], total_available_cents: 0, total_pending_cents: 0 });
  const [recentGifts, setRecentGifts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [membershipLimits, setMembershipLimits] = useState(null);

  useEffect(() => {
    const checkTheme = () => {
      const saved = localStorage.getItem('nearU_theme');
      setIsDark(saved !== 'light');
    };
    checkTheme();
    const interval = setInterval(checkTheme, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchAllData();
    
    // Check if we should open card activation
    if (location.state?.openCardActivation) {
      setShowVerificationModal(true);
    }
    
    // Check if there's a new gift to reveal
    if (location.state?.revealGift) {
      setRevealGift(location.state.revealGift);
      setShowGiftReveal(true);
    }
  }, [location]);

  const fetchAllData = async () => {
    try {
      const [cardRes, balanceRes, walletRes, catRes, limitsRes] = await Promise.all([
        fetch(`${API}/card/info`),
        fetch(`${API}/card/balances`),
        fetch(`${API}/gifts/wallet`),
        fetch(`${API}/card/categories`),
        fetch(`${API}/card/membership-limits`)
      ]);

      if (cardRes.ok) setCardInfo(await cardRes.json());
      if (balanceRes.ok) setBalances(await balanceRes.json());
      if (walletRes.ok) {
        const walletData = await walletRes.json();
        // Combine sent and received for recent activity
        const allGifts = [
          ...(walletData.gifts_sent || []).map(g => ({ ...g, type: 'sent' })),
          ...(walletData.gifts_received || []).map(g => ({ ...g, type: 'received' }))
        ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);
        setRecentGifts(allGifts);
      }
      if (catRes.ok) setCategories(await catRes.json());
      if (limitsRes.ok) setMembershipLimits(await limitsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const colors = {
    background: isDark ? '#0D0D0D' : '#FDFCFA',
    cardBg: isDark ? 'rgba(26, 15, 10, 0.95)' : 'rgba(255, 255, 255, 0.98)',
    text: isDark ? '#E6D7C3' : '#1A1410',
    textMuted: isDark ? '#8A7355' : '#4A4035',
    textAccent: isDark ? '#A57C33' : '#8B6914',
    border: isDark ? 'rgba(165, 124, 51, 0.2)' : 'rgba(139, 105, 20, 0.2)',
    accent: isDark ? '#D4AF37' : '#8B6914',
    gold: isDark ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)' : 'linear-gradient(135deg, #A57C33 0%, #8B6914 100%)'
  };

  const formatCurrency = (cents) => {
    if (!cents && cents !== 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cents / 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleCategorySelect = (category) => {
    navigate('/send-gift/select', { 
      state: { 
        giftCategory: category.id,
        categoryName: category.name,
        categoryEmoji: category.emoji,
        categoryImage: category.image,
        suggestedAmounts: category.suggested_amounts,
        minAmount: category.min_amount_cents,
        maxAmount: category.max_amount_cents
      }
    });
  };

  const handleVerificationSuccess = () => {
    fetchAllData();
    setShowVerificationModal(false);
  };

  const handleFreezeCard = async () => {
    try {
      const res = await fetch(`${API}/card/freeze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          freeze: !cardInfo?.is_frozen,
          reason: cardInfo?.is_frozen ? 'User unfreeze' : 'User freeze'
        })
      });
      if (res.ok) fetchAllData();
    } catch (error) {
      console.error('Error toggling freeze:', error);
    }
  };

  const getTierGradient = (tier) => {
    switch(tier) {
      case 'diamond': return 'linear-gradient(135deg, #E8F4F8 0%, #B9F2FF 30%, #89CFF0 70%, #00BFFF 100%)';
      case 'gold': return 'linear-gradient(135deg, #FFF8E1 0%, #FFD700 30%, #DAA520 70%, #B8860B 100%)';
      default: return 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 30%, #BDBDBD 70%, #9E9E9E 100%)';
    }
  };

  const getTierTextColor = (tier) => {
    return tier === 'gold' ? '#5D4E37' : tier === 'diamond' ? '#1A5276' : '#424242';
  };

  // Featured categories (show 4 main ones prominently)
  const featuredCategories = categories.slice(0, 4);
  const moreCategories = categories.slice(4);

  // Show Coming Soon page if gifting is disabled
  if (!FEATURES.gifting) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        paddingBottom: '100px', 
        background: isDark 
          ? 'linear-gradient(180deg, #0D0D0D 0%, #1A0F0A 50%, #0D0D0D 100%)'
          : 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)'
      }}>
        {/* Header */}
        <div style={{ 
          padding: '16px 20px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <NearULogo size="small" showText={true} />
        </div>

        {/* Page Title */}
        <div style={{ padding: '0 20px 20px', textAlign: 'center' }}>
          <h1 style={{ 
            fontFamily: 'Cinzel, serif', 
            fontSize: '28px',
            fontWeight: '600',
            color: colors.text,
            margin: 0
          }}>
            Gifts
          </h1>
        </div>

        {/* Coming Soon Content */}
        <div style={{ 
          padding: '40px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh'
        }}>
          {/* Coming Soon Icon */}
          <div style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.15) 0%, rgba(165, 124, 51, 0.1) 100%)',
            border: '2px solid rgba(215, 181, 109, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '32px',
            position: 'relative'
          }}>
            <Gift size={48} color="#D7B56D" />
            <div style={{
              position: 'absolute',
              bottom: '0',
              right: '0',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #4A4039, #2D2520)',
              border: '2px solid rgba(215, 181, 109, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Clock size={20} color="#D7B56D" />
            </div>
          </div>

          {/* Coming Soon Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            background: 'linear-gradient(135deg, #6B5B4D, #4A4039)',
            borderRadius: '20px',
            marginBottom: '24px'
          }}>
            <Lock size={14} color="#E6D7C3" />
            <span style={{
              fontSize: '12px',
              fontWeight: '700',
              color: '#E6D7C3',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Coming Soon
            </span>
          </div>

          {/* Title */}
          <h2 style={{
            fontFamily: 'Cinzel, serif',
            fontSize: '26px',
            fontWeight: '600',
            color: colors.text,
            margin: '0 0 16px 0',
            textAlign: 'center'
          }}>
            Luxury Gifting Experience
          </h2>

          {/* Description */}
          <p style={{
            color: colors.textMuted,
            fontSize: '15px',
            lineHeight: '1.7',
            textAlign: 'center',
            maxWidth: '320px',
            margin: '0 0 32px 0'
          }}>
            Send unforgettable gifts to the people who matter most. 
            Drinks, dining, spa experiences, and more — all delivered 
            through our elegant NearU Card.
          </p>

          {/* Feature Preview Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            width: '100%',
            maxWidth: '340px',
            marginBottom: '32px'
          }}>
            {[
              { icon: '🍸', label: 'Drinks', desc: '$20 - $500' },
              { icon: '🍽️', label: 'Dinner', desc: '$100 - $5,000' },
              { icon: '💆', label: 'Spa', desc: '$50 - $3,000' },
              { icon: '🛍️', label: 'Shopping', desc: '$50 - $5,000' }
            ].map((item, idx) => (
              <div key={idx} style={{
                padding: '16px',
                background: 'rgba(26, 15, 10, 0.6)',
                borderRadius: '14px',
                border: '1px solid rgba(165, 124, 51, 0.15)',
                opacity: 0.6
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>{item.icon}</div>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#8A7355',
                  marginBottom: '4px'
                }}>
                  {item.label}
                </div>
                <div style={{ fontSize: '11px', color: '#6B5B4D' }}>{item.desc}</div>
              </div>
            ))}
          </div>

          {/* NearU Card Preview */}
          <div style={{
            padding: '20px',
            background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.08) 0%, rgba(165, 124, 51, 0.04) 100%)',
            borderRadius: '20px',
            border: '1px solid rgba(215, 181, 109, 0.15)',
            textAlign: 'center',
            width: '100%',
            maxWidth: '340px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '12px'
            }}>
              <CreditCard size={20} color="#D7B56D" />
              <span style={{
                fontFamily: 'Cinzel, serif',
                fontSize: '16px',
                fontWeight: '600',
                color: '#D7B56D'
              }}>
                NearU Card
              </span>
            </div>
            <p style={{
              color: '#A57C33',
              fontSize: '13px',
              lineHeight: '1.6',
              margin: 0
            }}>
              One card for every gift. Recipients can spend their gifts 
              anywhere with the NearU prepaid card.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      paddingBottom: '100px', 
      background: isDark 
        ? 'linear-gradient(180deg, #0D0D0D 0%, #1A0F0A 50%, #0D0D0D 100%)'
        : 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)'
    }}>
      {/* Header */}
      <div style={{ 
        padding: '16px 20px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <NearULogo size="small" showText={true} />
        
        {/* Membership Badge - using new component */}
        {membershipLimits && (
          <MembershipBadge tier={membershipLimits.tier} size="medium" />
        )}
      </div>

      {/* Page Title */}
      <div style={{ padding: '0 20px 20px', textAlign: 'center' }}>
        <h1 style={{ 
          fontFamily: 'Cinzel, serif', 
          fontSize: '28px',
          fontWeight: '600',
          color: colors.text,
          margin: 0
        }}>
          Gifts
        </h1>
      </div>

      {/* ============================================ */}
      {/* SECTION 1: SEND A GIFT HERO */}
      {/* ============================================ */}
      <div style={{ padding: '0 16px 24px' }}>
        <div 
          onClick={() => navigate('/send-gift/select')}
          data-testid="send-gift-hero"
          style={{
            position: 'relative',
            height: '160px',
            borderRadius: '20px',
            overflow: 'hidden',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #2C1B13 0%, #1A0F0A 100%)',
            border: '1px solid rgba(215, 181, 109, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 60px rgba(215, 181, 109, 0.08)'
          }}
        >
          {/* Background pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `radial-gradient(circle at 80% 20%, rgba(215, 181, 109, 0.15) 0%, transparent 50%),
                              radial-gradient(circle at 20% 80%, rgba(215, 181, 109, 0.1) 0%, transparent 40%)`,
            pointerEvents: 'none'
          }} />
          
          {/* Content */}
          <div style={{
            position: 'relative',
            height: '100%',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: colors.gold,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(215, 181, 109, 0.4)'
                }}>
                  <Gift size={22} color="#2C1B13" />
                </div>
                <h2 style={{
                  fontFamily: 'Cinzel, serif',
                  fontSize: '22px',
                  fontWeight: '600',
                  color: '#D7B56D',
                  margin: 0
                }}>
                  Send a Gift
                </h2>
              </div>
              <p style={{
                color: '#A57C33',
                fontSize: '14px',
                margin: 0,
                maxWidth: '240px'
              }}>
                Make someone's day unforgettable
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                color: '#D7B56D',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                Choose an experience
              </span>
              <ArrowRight size={18} color="#D7B56D" />
            </div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* SECTION 2: GIFT EXPERIENCES */}
      {/* ============================================ */}
      <div style={{ padding: '0 16px 24px' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '16px'
        }}>
          <h3 style={{
            fontFamily: 'Cinzel, serif',
            fontSize: '18px',
            fontWeight: '600',
            color: colors.text,
            margin: 0
          }}>
            Gift Experiences
          </h3>
          <button
            onClick={() => navigate('/gift-categories')}
            style={{
              background: 'none',
              border: 'none',
              color: colors.accent,
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            View All <ChevronRight size={16} />
          </button>
        </div>

        {/* Featured Categories Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '12px',
          marginBottom: '12px'
        }}>
          {featuredCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategorySelect(cat)}
              data-testid={`category-${cat.id}`}
              style={{
                position: 'relative',
                height: '140px',
                borderRadius: '16px',
                border: cat.premium ? '2px solid rgba(215, 181, 109, 0.5)' : `1px solid ${colors.border}`,
                background: colors.cardBg,
                cursor: 'pointer',
                overflow: 'hidden',
                textAlign: 'left',
                padding: 0
              }}
            >
              {/* Background Image */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${cat.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }} />
              
              {/* Gradient Overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: isDark 
                  ? 'linear-gradient(180deg, rgba(26, 15, 10, 0.3) 0%, rgba(26, 15, 10, 0.95) 100%)'
                  : 'linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.95) 100%)'
              }} />

              {/* Content */}
              <div style={{
                position: 'relative',
                height: '100%',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end'
              }}>
                {cat.premium && (
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    padding: '4px 8px',
                    background: colors.gold,
                    borderRadius: '8px',
                    fontSize: '9px',
                    fontWeight: '700',
                    color: '#2C1B13',
                    textTransform: 'uppercase'
                  }}>
                    VIP
                  </div>
                )}
                
                <span style={{ fontSize: '24px', marginBottom: '4px' }}>{cat.emoji}</span>
                <span style={{
                  fontSize: '15px',
                  fontWeight: '700',
                  color: colors.text,
                  fontFamily: 'Cinzel, serif'
                }}>
                  {cat.name}
                </span>
                <span style={{
                  fontSize: '11px',
                  color: colors.textMuted,
                  marginTop: '2px'
                }}>
                  {cat.min_display} – {cat.max_display}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* More Categories Row */}
        {moreCategories.length > 0 && (
          <div style={{
            display: 'flex',
            gap: '10px',
            overflowX: 'auto',
            paddingBottom: '8px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            {moreCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategorySelect(cat)}
                data-testid={`category-${cat.id}`}
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 16px',
                  background: colors.cardBg,
                  border: cat.premium ? '2px solid rgba(215, 181, 109, 0.4)' : `1px solid ${colors.border}`,
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                <span style={{ fontSize: '20px' }}>{cat.emoji}</span>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    color: colors.text,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    {cat.name}
                    {cat.premium && (
                      <span style={{
                        fontSize: '8px',
                        padding: '2px 5px',
                        background: colors.gold,
                        borderRadius: '4px',
                        color: '#2C1B13',
                        fontWeight: '700'
                      }}>VIP</span>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textMuted }}>
                    {cat.min_display}+
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* SECTION 3: YOUR NEARU CARD */}
      {/* ============================================ */}
      <div style={{ padding: '0 16px 24px' }}>
        <h3 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '18px',
          fontWeight: '600',
          color: colors.text,
          margin: '0 0 16px 0'
        }}>
          Your NearU Card
        </h3>

        {(!cardInfo?.has_card) ? (
          /* LOCKED STATE */
          <div 
            data-testid="nearu-card-locked"
            style={{
              background: colors.cardBg,
              borderRadius: '20px',
              border: `1px solid ${colors.border}`,
              padding: '24px',
              textAlign: 'center'
            }}
          >
            {/* Preview Card */}
            <div style={{ 
              position: 'relative',
              opacity: 0.6,
              transform: 'scale(0.9)',
              marginBottom: '20px'
            }}>
              <NearUCard 
                firstName="YOUR NAME"
                lastFour="••••"
                expiry="••/••"
                isActive={false}
              />
              {/* Lock Overlay */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid rgba(215, 181, 109, 0.4)'
              }}>
                <Lock size={28} color="#D7B56D" />
              </div>
            </div>

            <h4 style={{
              fontFamily: 'Cinzel, serif',
              fontSize: '20px',
              color: colors.accent,
              margin: '0 0 8px 0'
            }}>
              One card for every gift
            </h4>

            <p style={{
              color: colors.textMuted,
              fontSize: '14px',
              lineHeight: '1.6',
              margin: '0 0 20px 0',
              maxWidth: '280px',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>
              Verify your identity once to activate your NearU Card and access eligible gifts.
            </p>

            {/* Pending gifts notice */}
            {balances.total_pending_cents > 0 && (
              <div style={{
                background: 'rgba(255, 193, 7, 0.1)',
                border: '1px solid rgba(255, 193, 7, 0.3)',
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                justifyContent: 'center'
              }}>
                <Gift size={18} color="#FFC107" />
                <span style={{ color: '#FFC107', fontSize: '14px', fontWeight: '600' }}>
                  {formatCurrency(balances.total_pending_cents)} in gifts waiting!
                </span>
              </div>
            )}

            <button
              onClick={() => setShowVerificationModal(true)}
              data-testid="activate-card-btn"
              style={{
                width: '100%',
                padding: '16px',
                background: colors.gold,
                border: 'none',
                borderRadius: '14px',
                color: '#2C1B13',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(215, 181, 109, 0.4)',
                marginBottom: '12px'
              }}
            >
              ACTIVATE YOUR NEARU CARD
            </button>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}>
              <Lock size={14} color={colors.textMuted} />
              <span style={{ color: colors.textMuted, fontSize: '12px' }}>
                Identity verification required
              </span>
            </div>
          </div>
        ) : (
          /* ACTIVE STATE */
          <div 
            data-testid="nearu-card-active"
            style={{
              background: colors.cardBg,
              borderRadius: '20px',
              border: `1px solid ${colors.border}`,
              overflow: 'hidden'
            }}
          >
            {/* Card Display */}
            <div style={{ 
              padding: '20px',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <div style={{ transform: 'scale(0.95)' }}>
                <NearUCard 
                  firstName={cardInfo.card_holder_name || 'MEMBER'}
                  lastFour={cardInfo.last_four || '0000'}
                  expiry={cardInfo.expiry_display || '00/00'}
                  isActive={true}
                  isFrozen={cardInfo.is_frozen}
                />
              </div>
            </div>

            {/* Balance Display */}
            <div style={{
              padding: '0 24px 20px',
              textAlign: 'center'
            }}>
              <div style={{ 
                color: colors.textMuted, 
                fontSize: '13px', 
                marginBottom: '4px',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Available Gifts
              </div>
              <div style={{ 
                fontSize: '36px', 
                fontWeight: '700',
                fontFamily: 'Cinzel, serif',
                background: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {formatCurrency(balances.total_available_cents)}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1px',
              background: colors.border
            }}>
              <button
                onClick={() => {/* Apple Wallet */}}
                data-testid="apple-wallet-btn"
                style={{
                  padding: '16px',
                  background: colors.cardBg,
                  border: 'none',
                  color: colors.text,
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Apple size={18} />
                Add to Wallet
              </button>
              <button
                onClick={() => navigate('/gift-activity')}
                data-testid="view-gifts-btn"
                style={{
                  padding: '16px',
                  background: colors.cardBg,
                  border: 'none',
                  color: colors.text,
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Eye size={18} />
                View Gifts
              </button>
            </div>

            {/* Secondary Actions */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '24px',
              padding: '16px',
              borderTop: `1px solid ${colors.border}`
            }}>
              <button
                onClick={handleFreezeCard}
                data-testid="freeze-card-btn"
                style={{
                  background: 'none',
                  border: 'none',
                  color: cardInfo.is_frozen ? '#4CAF50' : colors.textMuted,
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Snowflake size={14} />
                {cardInfo.is_frozen ? 'Unfreeze' : 'Freeze Card'}
              </button>
              <button
                onClick={() => navigate('/card-activity')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Clock size={14} />
                Activity
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* SECTION 4: RECENT GIFTS */}
      {/* ============================================ */}
      {recentGifts.length > 0 && (
        <div style={{ padding: '0 16px 24px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
            <h3 style={{
              fontFamily: 'Cinzel, serif',
              fontSize: '18px',
              fontWeight: '600',
              color: colors.text,
              margin: 0
            }}>
              Recent Gifts
            </h3>
            <button
              onClick={() => navigate('/gift-activity')}
              style={{
                background: 'none',
                border: 'none',
                color: colors.accent,
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              View All <ChevronRight size={16} />
            </button>
          </div>

          <div style={{
            background: colors.cardBg,
            borderRadius: '16px',
            border: `1px solid ${colors.border}`,
            overflow: 'hidden'
          }}>
            {recentGifts.slice(0, 3).map((gift, index) => (
              <div
                key={gift.id || index}
                onClick={() => navigate(`/gift/${gift.id}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '14px 16px',
                  cursor: 'pointer',
                  borderBottom: index < Math.min(recentGifts.length, 3) - 1 ? `1px solid ${colors.border}` : 'none'
                }}
              >
                {/* Avatar/Icon */}
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: gift.type === 'sent' 
                    ? 'rgba(33, 150, 243, 0.15)'
                    : 'rgba(76, 175, 80, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}>
                  {gift.category?.includes('dinner') ? '🍽' :
                   gift.category?.includes('drink') ? '🍸' :
                   gift.category?.includes('spa') ? '💆' :
                   gift.category?.includes('bottle') ? '🍾' :
                   gift.category?.includes('shop') ? '🛍' :
                   gift.category?.includes('travel') ? '✈️' :
                   gift.category?.includes('entertainment') ? '🎟' :
                   gift.category?.includes('celebration') ? '🎉' :
                   '✨'}
                </div>
                
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    color: colors.text, 
                    fontSize: '14px', 
                    fontWeight: '600',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {gift.type === 'sent' 
                      ? `To ${gift.recipient_name || 'Someone'}` 
                      : `From ${gift.sender_name || 'Someone'}`}
                  </div>
                  <div style={{ 
                    color: colors.textMuted, 
                    fontSize: '12px', 
                    marginTop: '2px' 
                  }}>
                    {gift.category?.replace('_', ' ')} • {formatDate(gift.created_at)}
                  </div>
                </div>

                {/* Amount */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ 
                    color: gift.type === 'sent' ? colors.text : '#4CAF50',
                    fontSize: '15px', 
                    fontWeight: '700'
                  }}>
                    {gift.type === 'sent' ? '-' : '+'}{formatCurrency(gift.gross_gift_amount_cents)}
                  </div>
                  <div style={{
                    fontSize: '10px',
                    color: gift.type === 'sent' ? '#2196F3' : '#4CAF50',
                    textTransform: 'uppercase',
                    fontWeight: '600'
                  }}>
                    {gift.type}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State for No Recent Activity */}
      {recentGifts.length === 0 && !loading && (
        <div style={{ padding: '0 16px 24px' }}>
          <div style={{
            background: colors.cardBg,
            borderRadius: '16px',
            border: `1px solid ${colors.border}`,
            padding: '32px 24px',
            textAlign: 'center'
          }}>
            <Sparkles size={40} color={colors.textMuted} style={{ opacity: 0.5, marginBottom: '12px' }} />
            <p style={{ 
              color: colors.textMuted, 
              fontSize: '15px',
              margin: '0 0 4px 0',
              fontWeight: '500'
            }}>
              No gifts yet
            </p>
            <p style={{
              color: colors.textMuted,
              fontSize: '13px',
              margin: 0,
              opacity: 0.8
            }}>
              Something unforgettable could be one tap away
            </p>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* FOOTER: TIER INFO */}
      {/* ============================================ */}
      {membershipLimits && (
        <div style={{ padding: '0 16px 16px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 16px',
            background: 'rgba(215, 181, 109, 0.05)',
            borderRadius: '10px'
          }}>
            <span style={{ color: colors.textMuted, fontSize: '12px' }}>Gift Limits:</span>
            {Object.entries(membershipLimits.all_tiers).map(([tier, limits], index) => (
              <span 
                key={tier}
                style={{ 
                  color: tier === membershipLimits.tier ? colors.accent : colors.textMuted,
                  fontSize: '12px',
                  fontWeight: tier === membershipLimits.tier ? '700' : '400'
                }}
              >
                {tier.charAt(0).toUpperCase() + tier.slice(1)} {limits.max}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Legal Notice */}
      <div style={{ padding: '8px 16px 0' }}>
        <p style={{ 
          color: colors.textMuted, 
          fontSize: '9px',
          lineHeight: '1.4',
          textAlign: 'center',
          opacity: 0.6
        }}>
          Financial functionality is subject to final sponsor-bank, issuing-program, 
          regulatory and legal approval.
        </p>
      </div>

      {/* Identity Verification Modal */}
      <IdentityVerificationModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        onSuccess={handleVerificationSuccess}
      />

      {/* Gift Reveal Modal */}
      <GiftRevealModal
        isOpen={showGiftReveal}
        onClose={() => {
          setShowGiftReveal(false);
          setRevealGift(null);
        }}
        gift={revealGift}
        senderName={revealGift?.sender_name}
        senderPhoto={revealGift?.sender_photo}
        recipientHasCard={cardInfo?.has_card}
      />
    </div>
  );
};

export default GiftsPage;
